import React, { useEffect } from "react";
import { getColors, MobergButton, MobergColumn, MobergDropdown, MobergDropdownSize, MobergFontFamily, MobergRow, MobergTheme } from "../../../../../../../../../Moberg";
import { useEventReview, useEventReviewForm } from "../../../../../../../../../Providers/EventReviewProvider";
import { allWindowsDispatch } from "../../../../../Atoms/Visualizations";
import { WindowClickedEvent } from "../../../../../Types/LinkedWindowInfo";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { canOpenAnnotationsModalAtom } from "../../../../../Atoms/Annotations";
import { formatDate } from "../../../../../utils";
import { currentPatientFileInfoAtom } from "../../../../../Atoms/PatientFile";
import { FREQUENCY_OPTIONS, PREVALENCE_OPTIONS } from "../../../../../Types/EventReview"

interface IICPatternFeaturesFormProps {
	style?: React.CSSProperties
}

const ClickToSetDateTimeInput: React.FC = () => {
	const eventReviewForm = useEventReviewForm()
	const eventReview = useEventReview()
	const patientInfo = useRecoilValue(currentPatientFileInfoAtom)
	const red = getColors(MobergTheme.RED).main
	const canOpenAnnotationsModal = useSetRecoilState(canOpenAnnotationsModalAtom)
	const validationErrorKey = "Time point of max frequency"
	const errorMessage = eventReviewForm.errors.get(validationErrorKey)
	const showInfoMessage = eventReviewForm.isSetDateTimeActive && !errorMessage

	useEffect(() => {
		const startTime = eventReview.currentAnnotation?.start_time
		const endTime = eventReview.currentAnnotation?.end_time

		if (!startTime || !endTime) {
			return
		}

		if (eventReviewForm.isSetDateTimeActive) {
			allWindowsDispatch.on("click", (event: WindowClickedEvent) => {
				const timePointOfMaxFrequency = event.lastHoveredDate?.getTime()
				if (timePointOfMaxFrequency) {
					eventReviewForm.setTimeOfMaxFrequency(timePointOfMaxFrequency)
					eventReviewForm.setIsSetDateTimeActive(false)
					eventReviewForm.updateValidationError(validationErrorKey, undefined)
					canOpenAnnotationsModal(true)
					allWindowsDispatch.on("click", null)
				} else {
					eventReviewForm.updateValidationError(validationErrorKey, "No time point selected. Click on a valid display to set the time.")
				}
			})

		} else {
			allWindowsDispatch.on("click", null)
		}
	}, [canOpenAnnotationsModal, eventReview.currentAnnotation?.end_time, eventReview.currentAnnotation?.start_time, eventReviewForm, eventReviewForm.isSetDateTimeActive])

	function enableClickToSetDatetime() {
		eventReviewForm.setIsSetDateTimeActive(true)
		canOpenAnnotationsModal(false)
	}

	return (
		<>
			<span style={{ fontFamily: MobergFontFamily.REGULAR, whiteSpace: "nowrap", textAlign: "left", color: "var(--color-text-primary)" }}><strong>Time point of max frequency</strong></span>
			<div>
				<MobergRow gap="8px">
					{eventReviewForm.timeOfMaxFrequency && (
						<span style={{ fontFamily: MobergFontFamily.REGULAR, whiteSpace: "nowrap", color: "var(--color-text-primary)" }}>
							{formatDate(new Date(eventReviewForm.timeOfMaxFrequency), {
								timeZone: patientInfo.timeZone,
								seconds: true,
								milliseconds: true,
								hourCycle: "h23",
							})}
						</span>
					)}

					<MobergButton
						theme={MobergTheme.BLUE}
						onClick={enableClickToSetDatetime}
						style={{ padding: "2px 4px" }}
					>
						Click to set
					</MobergButton>
				</MobergRow>
			</div>
			{errorMessage && <p style={{ fontFamily: MobergFontFamily.REGULAR, color: red, gridColumn: "1 / -1", margin: 0 }}>{errorMessage}</p>}
			{showInfoMessage && <p style={{ fontFamily: MobergFontFamily.REGULAR, color: "var(--color-text-primary)", gridColumn: "1 / -1", margin: 0 }}>Click on a display with a timeline to select a time.</p>}
		</>
	)
}

export const IICPatternFeaturesForm: React.FC<IICPatternFeaturesFormProps> = () => {
	const eventReviewForm = useEventReviewForm()
	const eventReview = useEventReview()
	const locationDropdownOptions = ['Left', 'Right', 'Bilat', 'Bilateral-Independent', 'Multifocal']

	const buildInputLabel = (label: string) => (
		<span style={{ fontFamily: MobergFontFamily.REGULAR, whiteSpace: "nowrap", textAlign: "left", color: "var(--color-text-primary)" }}>
			<strong>{label}</strong>
		</span>
	)

	const annotationId = eventReview.currentAnnotation?.id

	if (!annotationId) {
		return null
	}

	return (
		<MobergColumn verticalAlign="start" style={{ fontFamily: MobergFontFamily.REGULAR }} gap="4px">
			<div style={{ display: "grid", gridTemplateColumns: "minmax(170px, max-content) 1fr", gap: "12px 16px", alignItems: "center" }}>
				{buildInputLabel("Location")}
				<MobergDropdown
					selectedValue={eventReviewForm.location}
					onChange={eventReviewForm.setLocation}
					options={locationDropdownOptions.map(location => ({ label: location, value: location }))}
					width={170}
					size={MobergDropdownSize.SMALL}
				/>

				{buildInputLabel("Prevalence")}
				<MobergDropdown
					selectedValue={eventReviewForm.prevalence}
					onChange={eventReviewForm.setPrevalence}
					options={PREVALENCE_OPTIONS.map(option => ({ label: option, value: option }))}
					width={170}
					size={MobergDropdownSize.SMALL}
				/>

				{buildInputLabel("Max frequency")}
				<MobergDropdown
					selectedValue={eventReviewForm.maxFrequency}
					onChange={eventReviewForm.setMaxFrequency}
					options={FREQUENCY_OPTIONS.map(option => ({ label: option, value: option }))}
					width={170}
					size={MobergDropdownSize.SMALL}
				/>

				{buildInputLabel("Predominant frequency")}
				<MobergDropdown
					selectedValue={eventReviewForm.predominantFrequency}
					onChange={eventReviewForm.setPredominantFrequency}
					options={FREQUENCY_OPTIONS.map(option => ({ label: option, value: option }))}
					width={170}
					size={MobergDropdownSize.SMALL}
				/>

				<ClickToSetDateTimeInput />
			</div>
		</MobergColumn>
	)
}
